import { useQuery } from '@tanstack/react-query';
import axiosClient from '@/lib/auth';
import { MonitorAlertsSearched } from '../types';

interface AlertSearchFilters {
  searchText?: string;
  searchType: 'alert' | 'site' | 'customer';
  startDate: Date | undefined;
  endDate: Date | undefined;
  pageSize?: number;
}

const buildSearchParams = (filters: AlertSearchFilters, totalCount?: number) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const params: Record<string, any> = {
    pageNumber: 1,
    pageSize: totalCount || 1, // First request gets 1 item to get total count
    sortBy: 'occurred',
    sortDir: filters.startDate || filters.endDate ? 'Ascending' : 'Descending', // Sort by ascending when using date filters
    filterOperator: 'Or',
  };

  const searchFilters = [];

  // Add date range filters if provided
  if (filters.startDate || filters.endDate) {
    if (filters.startDate) {
      searchFilters.push({
        action: 'Gt',
        field: 'occurred',
        value: filters.startDate.toISOString().replace('Z', '+00:00'),
      });
    }

    if (filters.endDate) {
      searchFilters.push({
        action: 'Lt',
        field: 'occurred',
        value: filters.endDate.toISOString().replace('Z', '+00:00'),
      });
    }
  }

  // Handle different search types
  if (filters.searchText) {
    switch (filters.searchType) {
      case 'alert':
        // Normalize alert ID format
        searchFilters.push({
          action: 'ILike',
          field: 'alertId',
          value: filters.searchText.replace(/^([aA])[-\s]?(\d+)$/, 'A-$2').toUpperCase(),
        });
        break;

      case 'site':
        searchFilters.push({
          action: 'ILike',
          field: 'projectName',
          value: filters.searchText,
        });
        break;

      case 'customer':
        searchFilters.push({
          action: 'ILike',
          field: 'companyName',
          value: filters.searchText,
        });
        break;
    }
  }

  if (searchFilters.length > 0) {
    params['Filter.Filters'] = searchFilters;
  }

  console.log('API Search Params:', params);
  return params;
};

export const useAlertSearch = (filters: AlertSearchFilters) => {
  const hasFilters = Boolean(
    filters.searchText?.trim() || filters.startDate || filters.endDate
  );

  return useQuery({
    queryKey: ['alertSearch', filters],
    enabled: hasFilters,
    queryFn: async () => {
      // First request to get total count
      const countResponse = await axiosClient.get<MonitorAlertsSearched>(
        '/AlertMonitoring/search',
        {
          params: buildSearchParams(filters),
        }
      );

      // If we have a total count, make a second request to get all alerts
      if (countResponse.data.totalRows && countResponse.data.totalRows > 1) {
        const response = await axiosClient.get<MonitorAlertsSearched>('/AlertMonitoring/search', {
          params: buildSearchParams(filters, Math.min(countResponse.data.totalRows, 20)),
        });

        const alerts = response.data.rows || [];
        // Remove sortAlerts since the API is already sorting them
        const uniqueAlerts = alerts.filter(
          (alert, index, self) => index === self.findIndex((a) => a.id === alert.id)
        );

        return {
          alerts: uniqueAlerts.map((alert) => ({
            id: alert.id,
            alertId: alert.alertId,
            companyName: alert.companyName,
            projectName: alert.projectName,
            occurred: alert.occurred,
            status: alert.status,
            snapshot: alert.snapshot,
            actionedBy: alert.actionedBy,
          })),
          totalRows: response.data.totalRows || 0,
        };
      }

      // If no results or only one result, return the first response
      const alerts = countResponse.data.rows || [];
      const uniqueAlerts = alerts.filter(
        (alert, index, self) => index === self.findIndex((a) => a.id === alert.id)
      );

      return {
        alerts: uniqueAlerts.map((alert) => ({
          id: alert.id,
          alertId: alert.alertId,
          companyName: alert.companyName,
          projectName: alert.projectName,
          occurred: alert.occurred,
          status: alert.status,
          snapshot: alert.snapshot,
          actionedBy: alert.actionedBy,
        })),
        totalRows: countResponse.data.totalRows || 0,
      };
    },
  });
};
